import { type ComponentProps } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Button, DisplayText, Modal, type ModalDialogue, theme } from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationRestrictionModal = (props: Props) => {
  const { restrictionText, title, gotItButtonTitle, onRequestClose, ...modalProps } = props;

  const { formatMessage } = useIntl();

  const lowerCasedRestrictionText = restrictionText.toLowerCase();

  const formattedTitle = title
    ? title.replaceAll(DYNAMIC_RESTRICTION_TEXT, lowerCasedRestrictionText)
    : formatMessage(messages.title, { text: lowerCasedRestrictionText });

  const formattedGotItButtonTitle = gotItButtonTitle ?? formatMessage(messages.gotIt);

  return (
    <Modal withCloseBtn={false} onRequestClose={onRequestClose} {...modalProps}>
      <CustomizationModalContentContainer size="lg">
        <DisplayText sizeMatch={['32']}>{formattedTitle}</DisplayText>

        <View style={styles.ctaContainer}>
          <Button
            palette="kale"
            size="large-wide"
            style={styles.cta}
            accessibilityLabel={formattedGotItButtonTitle}
            onPress={onRequestClose}
          >
            <BodyText sizeMatch={['18']} style={styles.buttonText}>
              {formattedGotItButtonTitle}
            </BodyText>
          </Button>
        </View>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DYNAMIC_RESTRICTION_TEXT = '(restrictionText)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly title: { readonly text: MessageValue };
  readonly gotIt: NoMessageValues;
}>({
  title: {
    defaultMessage: 'Please note — this ingredient {text}.',
    description: 'Customization | Restriction modal | Text',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Customization | Restriction modal | Button title',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['2'],
  },
  cta: {
    minWidth: 246,
    height: 72,
  },
  buttonText: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restrictionText: string;
  title?: string;
  gotItButtonTitle?: string;
} & ComponentProps<typeof ModalDialogue>;
